/**
 * Channel constants and series builders for the High Rate tab — the 20 Hz
 * HTS grid stored in `data_gridded_hts` (`scope=grid_data_hts`). The channel
 * set mirrors the server's write-time allow-list DEFAULT_HTS_CHANNEL_LIST
 * (common/cscore/defaults/default_consts.py); the shared time axis is the
 * same `TimeRel_tG00` the standard grid uses, so `buildSeriesPoints` from the
 * Aux Sensor tab works unchanged.
 */
import { AuxPlotSeries, buildSeriesPoints } from '../RunAuxSensor/auxSensorChannels';
import { RunHealthObj } from '../../../types/Run';

type Gridded = Record<string, (number | null)[]> | null | undefined;

/**
 * Minimum wind5/aero5 raw-stream rate (Hz) for the High Rate tab to be worth
 * showing — above the ~8 Hz ANT page rate, comfortably below the 444 Hz BLE
 * HDR raw stream.
 */
export const HTS_WIND_MIN_HZ = 10;

/**
 * Whether the High Rate tab should be shown. An HTS grid existing
 * (`run.has_hts`) is necessary but not sufficient: a grid can be stale, or
 * triggered by IMU accel alone, holding nothing but low-rate channels
 * interpolated to 20 Hz. The tab shows only when the pipeline's HTS gate
 * actually detected high-rate content (`run_health_obj.hts_sources`): a
 * wind5/aero5 raw stream faster than 10 Hz, or — on a multi-magnet wheel
 * (magnet count > 1 on the run's calibration) — a genuinely high-rate
 * per-rev wheel series. Magnet count alone is NOT enough: over ANT pages
 * the per-rev events collapse to page rate (~4 Hz) regardless of magnets,
 * and `wheel_per_rev` is what says the events really arrived at rate.
 * Runs processed before `hts_sources` existed need a reprocess to pass.
 */
export function hasHighRateData(
  run: { has_hts?: boolean | null; run_health_obj?: RunHealthObj | null } | null | undefined,
  calibration: { wheel_magnet_count?: number; parameters_inherited?: any } | null | undefined,
): boolean {
  if (!run?.has_hts) return false;
  const magnetCount = calibration?.wheel_magnet_count
    ?? calibration?.parameters_inherited?.wheel_magnet_count?.[0]
    ?? 1;
  const sources = run?.run_health_obj?.hts_sources;
  const windHz = sources?.aero5_raw ?? 0;
  const wheelHz = sources?.wheel_per_rev ?? 0;
  return windHz > HTS_WIND_MIN_HZ || (magnetCount > 1 && wheelHz > 0);
}

const MS_TO_KMH = 3.6;

export const HTS_CHANNELS = {
  airSpeed: 'AirSpeedRaw_vG31',
  dynPres: 'DynPresRaw_pG31',
  airYaw: 'AirYawRaw_AG11',
  diffs: ['Diff1Raw_pG10', 'Diff2Raw_pG10', 'Diff3Raw_pG10'],
  // Rotational rev/s — safe to plot as-is. WhlLspd_v stays excluded: its
  // units differ by which pipeline path populated the wheel object (GPS
  // fallback vs real wheel sensor), so a fixed client-side conversion
  // would mislabel one of them.
  wheelSpeed: 'WhlRspd_n',
} as const;

/**
 * Per-magnet-pass wheel speed, carried UNGRIDDED in the HTS payload (each
 * series has its own time axis, already rebased to the grid's TimeRel
 * frame). The corrected series applies the resolved gap angles; the uniform
 * series is the same events under the 1/N-revolution assumption — overlaying
 * the two shows the angular-spacing correction working (or not: residual
 * 3-per-rev sawtooth in the corrected trace means the applied angles are
 * wrong). Only present on multi-magnet runs processed since the channels
 * were added.
 */
export const HTS_WHEEL_EVENT_CHANNELS = {
  corrected: { ts: 'WhlRspdEvtTs_t', value: 'WhlRspdEvt_n' },
  uniform: { ts: 'WhlRspdEvtUniTs_t', value: 'WhlRspdEvtUni_n' },
} as const;

/**
 * Points for an event-level series carrying its own time channel — unlike
 * `buildSeriesPoints`, which pins x to the shared grid axis.
 */
export function buildEventSeriesPoints(
  hts: Gridded,
  tsChannel: string,
  valueChannel: string,
): [number, number | null][] {
  if (!hts) return [];
  const time = hts[tsChannel];
  const values = hts[valueChannel];
  if (!time || !values) return [];
  const n = Math.min(time.length, values.length);
  const points: [number, number | null][] = [];
  for (let i = 0; i < n; i += 1) {
    const t = time[i];
    const v = values[i];
    if (t == null || Number.isNaN(t)) continue;
    points.push([t, v == null || Number.isNaN(v) ? null : v]);
  }
  return points;
}

/**
 * Point budget per plotted series. 20 Hz over a ~8-minute run is ~9k points
 * per channel; SVG ApexCharts stays responsive around this budget, and the
 * min/max decimation below preserves the spikes that matter in raw aero data.
 */
export const HTS_MAX_POINTS = 4000;

/**
 * Bucketed min/max decimation: each bucket contributes its extreme samples
 * (in time order), so peaks and troughs survive that a plain stride or mean
 * would flatten. Buckets that are entirely gaps contribute one null point to
 * keep the gap visible. Input under budget is returned untouched (referential
 * stability for `useMemo` consumers).
 */
export function downsampleMinMax(
  points: [number, number | null][],
  maxPoints: number = HTS_MAX_POINTS,
): [number, number | null][] {
  if (points.length <= maxPoints) return points;
  const bucketCount = Math.max(1, Math.floor(maxPoints / 2));
  const bucketSize = Math.ceil(points.length / bucketCount);
  const out: [number, number | null][] = [];
  for (let start = 0; start < points.length; start += bucketSize) {
    const end = Math.min(start + bucketSize, points.length);
    let minPoint: [number, number] | undefined;
    let maxPoint: [number, number] | undefined;
    for (let i = start; i < end; i += 1) {
      const value = points[i][1];
      if (value == null) continue;
      if (minPoint === undefined || value < minPoint[1]) minPoint = points[i] as [number, number];
      if (maxPoint === undefined || value > maxPoint[1]) maxPoint = points[i] as [number, number];
    }
    if (minPoint === undefined || maxPoint === undefined) {
      out.push([points[start][0], null]);
      continue;
    }
    const [first, second] = minPoint[0] <= maxPoint[0] ? [minPoint, maxPoint] : [maxPoint, minPoint];
    out.push(first);
    if (second !== first) out.push(second);
  }
  return out;
}

function series(
  hts: Gridded,
  key: string,
  label: string,
  channel: string,
  transform?: (v: number) => number,
): AuxPlotSeries {
  return { key, label, points: downsampleMinMax(buildSeriesPoints(hts, channel, { transform })) };
}

export interface HtsPlotSpec {
  title: string;
  unit: string;
  build: (hts: Gridded) => AuxPlotSeries[];
}

const toKmh = (v: number) => v * MS_TO_KMH;

/**
 * The tab's plots, in display order. One descriptor per plot keeps the
 * title/unit next to the channels they describe, and lets RunHighRate render
 * the tab by mapping over this list instead of naming every plot twice.
 */
export const HTS_PLOTS: HtsPlotSpec[] = [
  {
    // Air speed is m/s at source, plotted in km/h.
    title: 'Air Speed (Raw)',
    unit: 'km/h',
    build: (hts) => [series(hts, 'hts-air-speed', 'Air Speed (Raw)', HTS_CHANNELS.airSpeed, toKmh)],
  },
  {
    title: 'Dynamic Pressure (Raw)',
    unit: 'Pa',
    build: (hts) => [series(hts, 'hts-dyn-pres', 'Dynamic Pressure (Raw)', HTS_CHANNELS.dynPres)],
  },
  {
    // The three raw differential pressures of the probe.
    title: 'Differential Pressures (Raw)',
    unit: 'Pa',
    build: (hts) => HTS_CHANNELS.diffs.map(
      (channel, i) => series(hts, `hts-diff-${i + 1}`, `Diff ${i + 1}`, channel),
    ),
  },
  {
    title: 'Air Yaw (Raw)',
    unit: '°',
    build: (hts) => [series(hts, 'hts-air-yaw', 'Air Yaw (Raw)', HTS_CHANNELS.airYaw)],
  },
  {
    // Rotational wheel speed in rev/s — the multi-magnet per-rev series
    // gridded at 20 Hz. Rendered only when the payload carries it (empty
    // plots are dropped by RunHighRate).
    title: 'Wheel Speed (Rotational)',
    unit: 'rev/s',
    build: (hts) => [series(hts, 'hts-wheel-speed', 'Wheel Speed', HTS_CHANNELS.wheelSpeed)],
  },
  {
    // The angular-spacing correction, per magnet pass: the same events
    // under the uniform 1/N assumption vs with the applied gap angles.
    // A flat corrected trace over a sawtoothed uniform trace = the
    // correction worked; sawtooth surviving in the corrected trace = the
    // applied angles (or their phase rotation) are wrong.
    title: 'Wheel Speed — Spacing Correction (per pass)',
    unit: 'rev/s',
    build: (hts) => [
      {
        key: 'hts-wheel-evt-uniform',
        label: 'Uniform spacing (raw)',
        points: downsampleMinMax(buildEventSeriesPoints(
          hts, HTS_WHEEL_EVENT_CHANNELS.uniform.ts, HTS_WHEEL_EVENT_CHANNELS.uniform.value)),
      },
      {
        key: 'hts-wheel-evt-corrected',
        label: 'Spacing corrected',
        points: downsampleMinMax(buildEventSeriesPoints(
          hts, HTS_WHEEL_EVENT_CHANNELS.corrected.ts, HTS_WHEEL_EVENT_CHANNELS.corrected.value)),
      },
    ],
  },
];

/** A plot is worth rendering only if some series has a real (non-null) point. */
export function plotHasData(seriesList: AuxPlotSeries[]): boolean {
  return seriesList.some((s) => s.points.some(([, v]) => v != null));
}
